import { useEffect, useState } from "react";
import {
  ProfileAction,
  UserAddress,
  digitalSubscriptions,
  useUserProfile,
} from "@providers/profile";
import Loading from "@components/ui/loading";
import SubscriptionInfo from "./SubscriptionInfo";
import PaymentInfo from "../payment-info";
import { SubscriptionProps, initialBillingState, BillingState } from "./Subscription.model";
import { AccountHeader, AccountSection } from "../Account.styled";

const Subscription = ({ isDigital, digitalDisclaimers, states }: SubscriptionProps) => {
  const [billingState, setBillingState] = useState<BillingState>(initialBillingState);
  const {
    getSubscription,
    getAddress,
    getCreditCard,
    dispatch,
    profileState: { authState, authToken, profile },
  } = useUserProfile();

  // Fetch membership page data (user subscription, credit card and address)
  useEffect(() => {
    async function fetchBillingInfo() {
      let subscription, card, address;

      if (digitalSubscriptions.includes(profile.subscription_tier)) {
        [card, address] = await Promise.all([
          await getCreditCard(authToken),
          await getAddress(authToken),
        ]);
      } else {
        [subscription, card, address] = await Promise.all([
          await getSubscription(authToken, profile.email),
          await getCreditCard(authToken),
          await getAddress(authToken),
        ]);
      }

      setBillingState({
        ...billingState,
        subscription,
        card,
        address,
        loading: false,
      });
    }

    // Only fetch if the user is logged in and has been fetched.
    if (profile.email) fetchBillingInfo();
  }, [profile.email]);

  // update authState when billingState.address updates
  useEffect(() => {
    if (
      !billingState.loading &&
      authState &&
      JSON.stringify(billingState.address) !== JSON.stringify(authState?.value?.user?.address)
    ) {
      const newAddress: UserAddress = billingState.address;
      dispatch({
        type: ProfileAction.UPDATE_AUTH_USER,
        profile: profile,
        authState: {
          ...authState,
          value: {
            ...authState?.value,
            user: {
              ...authState?.value?.user,
              address: newAddress,
            },
          },
        },
        track: {
          eventGroup: "account",
          eventName: "updated",
        },
      });
    }
  }, [billingState, authState]);

  return (
    <>
      <AccountSection>
        <AccountHeader>
          <h3>My Membership</h3>
        </AccountHeader>
        <SubscriptionInfo
          isDigital={isDigital}
          subscription={billingState.subscription}
          digitalDisclaimers={digitalDisclaimers}
          dispatchBilling={setBillingState}
        />
      </AccountSection>
      <AccountSection>
        <PaymentInfo
          isDigital={isDigital}
          digitalDisclaimer={digitalDisclaimers?.payment_info_disclaimer}
          states={states}
          address={billingState.address}
          card={billingState.card}
          dispatchBilling={setBillingState}
        />
      </AccountSection>
      <Loading visible={billingState.loading} />
    </>
  );
};

export default Subscription;
